import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import blogService, { BlogPost } from '../services/blogService';
import { setStructuredData, updateCanonicalLink, updateMetaTag } from '../utils/seoHelpers';
import './FloristBlog.css';

const META_DESCRIPTION =
  'Floristbloggen från Lillaflora är fylld av dukningsidéer, handplockade presenter och mysiga tips direkt från vår butik i Kramfors.';
const META_KEYWORDS = 'floristbloggen, florist blogg, dukningsidéer, presenter, lillaflora kramfors';

const formatDate = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('sv-SE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(date);
};

const estimateReadingTime = (html: string) => {
  const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return 2;
  const words = text.split(' ').length;
  return Math.max(2, Math.round(words / 180));
};

const curatedCards = [
  {
    title: 'Skapa en varm dukning',
    description: 'Hitta allt från linneservetter till porslin i vår kategori Dukning & Servering.',
    link: '/produkter?kategori=dukning-och-servering',
    label: 'Se dukningsfavoriter'
  },
  {
    title: 'Doftljus att ge bort',
    description: 'Välj mellan Sweeds, Majas Cottage och våra egna signaturdofter.',
    link: '/produkter?kategori=ljus',
    label: 'Utforska doftljus'
  },
  {
    title: 'Presenttips från Floristen',
    description: 'Välj mysiga muggar, koppar och presentvänliga detaljer från vårt sortiment.',
    link: '/produkter?kategori=muggar-och-koppar',
    label: 'Utforska presenttips'
  }
];

const FloristBlogList: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.title = 'Floristbloggen – Inspiration från Lillaflora';
    updateMetaTag('description', META_DESCRIPTION);
    updateMetaTag('keywords', META_KEYWORDS);
    updateCanonicalLink('https://lillaflora.nu/floristbloggen');
  }, []);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await blogService.getPublished();
        setPosts(data);
      } catch (err) {
        console.error(err);
        setError('Det gick inte att ladda Floristbloggen just nu.');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  useEffect(() => {
    if (!posts.length) {
      setStructuredData('floristbloggen-schema');
      return;
    }

    const structured = {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Floristbloggen',
      description: META_DESCRIPTION,
      url: 'https://lillaflora.nu/floristbloggen',
      inLanguage: 'sv-SE',
      blogPost: posts.slice(0, 6).map((post) => ({
        '@type': 'BlogPosting',
        headline: post.title,
        datePublished: post.published_at || post.created_at,
        dateModified: post.updated_at,
        image: post.hero_image || undefined,
        url: `https://lillaflora.nu/floristbloggen/${post.slug}`,
        description: post.short_description || '',
        author: {
          '@type': 'Person',
          name: 'Floristteamet på Lillaflora'
        }
      }))
    };

    setStructuredData('floristbloggen-schema', structured);
    return () => setStructuredData('floristbloggen-schema');
  }, [posts]);

  const featuredPost = posts[0];
  const otherPosts = useMemo(() => posts.slice(1), [posts]);

  return (
    <div className="florist-blog-page">
      <section className="blog-hero container">
        <p className="blog-hero-eyebrow">Floristbloggen • Mys från butiken i Kramfors</p>
        <h1>Floristbloggen – dukningar, dofter & handplockad inspiration</h1>
        <p className="blog-hero-lead">
          Följ med bakom kulisserna hos Lillaflora. Vi delar säsongens dukningar, hur vi stylar hyllor i butiken
          och tips på omtänksamma presenter som sprider värme hemma hos dig.
        </p>
        <div className="blog-hero-actions">
          <Link className="blog-cta-primary" to="/produkter">
            Utforska sortimentet
          </Link>
        </div>
        <div className="blog-hero-stats">
          <div>
            <span>Skapat i butiken</span>
            <strong>Äkta Florist-känsla</strong>
          </div>
          <div>
            <span>Senaste uppdatering</span>
            <strong>{featuredPost ? formatDate(featuredPost.published_at || featuredPost.created_at) : 'Varje vecka'}</strong>
          </div>
          <div>
            <span>Ämnen</span>
            <strong>Blommor · Dukning · Presenter</strong>
          </div>
        </div>
      </section>

      {loading && (
        <div className="blog-state container">
          <div className="blog-spinner" />
          <p>Vi fluffar kuddarna och laddar bloggen...</p>
        </div>
      )}

      {error && !loading && (
        <div className="blog-state blog-error container">
          <p>{error}</p>
          <button type="button" onClick={() => window.location.reload()}>
            Försök igen
          </button>
        </div>
      )}

      {!loading && !error && !posts.length && (
        <div className="blog-state blog-empty container">
          <h2>Här blir det mys snart</h2>
          <p>Vi skapar de första inläggen just nu. Under tiden är du varmt välkommen att kika runt i webbshoppen.</p>
          <Link to="/produkter" className="blog-cta-primary">
            Till butiken
          </Link>
        </div>
      )}

      {!loading && !error && featuredPost && (
        <section className="blog-featured container">
          {featuredPost.hero_image ? (
            <img src={featuredPost.hero_image} alt={featuredPost.title} loading="lazy" />
          ) : (
            <div className="blog-featured-placeholder" aria-hidden="true">
              Blomstrande inspiration
            </div>
          )}
          <div className="blog-featured-content">
            <p className="blog-tag">Aktuellt just nu</p>
            <h2>{featuredPost.title}</h2>
            <p>{featuredPost.short_description}</p>
            <div className="blog-meta">
              <span>{formatDate(featuredPost.published_at || featuredPost.created_at)}</span>
              <span>{estimateReadingTime(featuredPost.content || '')} min lästid</span>
            </div>
            <Link to={`/floristbloggen/${featuredPost.slug}`} className="blog-cta-primary">
              Läs artikeln
            </Link>
          </div>
        </section>
      )}

      {!loading && !error && !!otherPosts.length && (
        <section className="blog-grid container">
          {otherPosts.map((post) => (
            <article className="blog-card" key={post.id}>
              {post.hero_image ? (
                <img src={post.hero_image} alt={post.title} loading="lazy" />
              ) : (
                <div className="blog-card-image placeholder" aria-hidden="true">
                  <span>Kika in</span>
                </div>
              )}
              <div className="blog-card-content">
                <p className="blog-tag">Floristens tips</p>
                <h3>{post.title}</h3>
                <p>{post.short_description}</p>
                <div className="blog-meta">
                  <span>{formatDate(post.published_at || post.created_at)}</span>
                  <span>{estimateReadingTime(post.content || '')} min lästid</span>
                </div>
                <Link to={`/floristbloggen/${post.slug}`} className="blog-link">
                  Läs mer →
                </Link>
              </div>
            </article>
          ))}
        </section>
      )}

      <section className="blog-curated container">
        {curatedCards.map((card) => (
          <article className="blog-curated-card" key={card.title}>
            <p className="blog-tag">Floristens favoriter</p>
            <h3>{card.title}</h3>
            <p>{card.description}</p>
            <Link to={card.link} className="blog-cta-secondary">
              {card.label}
            </Link>
          </article>
        ))}
      </section>
    </div>
  );
};

export default FloristBlogList;
